import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, PlayCircle, CheckCircle, ChevronRight, Trophy, ChevronLeft, Maximize2, ChevronDown, FolderOpen } from 'lucide-react';
import api from '../../services/api';
import VideoPlayer from '../../components/student/VideoPlayer';
import VideoRating from '../../components/student/VideoRating';
import VideoComments from '../../components/student/VideoComments';

export default function StudentModulePlayerPage() {
  const { moduleId, videoId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [videos, setVideos] = useState([]);
  const [sections, setSections] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [theaterMode, setTheaterMode] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const loadModuleData = useCallback(async () => {
    try {
      console.log('📚 Carregando dados do módulo:', moduleId);
      const [moduleRes, videosRes, progressRes, sectionsRes] = await Promise.all([
        api.get(`/modules/${moduleId}`),
        api.get(`/videos/module/${moduleId}`),
        api.get(`/progress/module/${moduleId}`),
        api.get(`/sections/module/${moduleId}`)
      ]);

      setModule(moduleRes.data);
      const publishedVideos = videosRes.data.filter(v => v.is_published);
      setVideos(publishedVideos);
      setSections(sectionsRes.data);
      
      const progressMap = {};
      progressRes.data.forEach(p => {
        progressMap[p.video_id] = p;
      });
      setProgress(progressMap);
      
      // Expandir todas as seções por padrão
      setExpandedSections(new Set(sectionsRes.data.map(s => s.id)));
      
      console.log(`✅ Módulo carregado: ${publishedVideos.length} vídeos, ${sectionsRes.data.length} seções`);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  const loadVideo = useCallback(async (id) => {
    try {
      console.log('🎬 Carregando vídeo:', id);
      const { data } = await api.get(`/videos/${id}`);
      setCurrentVideo(data);
      navigate(`/student/module/${moduleId}/video/${id}`, { replace: true });
    } catch (error) {
      console.error('❌ Erro ao carregar vídeo:', error);
    }
  }, [moduleId, navigate]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);

  useEffect(() => {
    if (videos.length === 0) return;
    
    if (videoId) {
      const videoExists = videos.find(v => v.id === videoId);
      if (videoExists) {
        loadVideo(videoId);
      } else {
        loadVideo(videos[0].id);
      }
    } else {
      loadVideo(videos[0].id);
    }
  }, [videoId, videos, loadVideo]);

  const handleProgressUpdate = (percent) => {
    console.log(`📊 Progresso atualizado: ${percent.toFixed(1)}%`);
    
    if (currentVideo && currentVideo.duration_seconds) {
      const watchedSeconds = Math.floor((percent / 100) * currentVideo.duration_seconds);
      const isCompleted = percent >= 90;
      
      setProgress(prev => ({
        ...prev,
        [currentVideo.id]: {
          ...prev[currentVideo.id],
          watched_seconds: watchedSeconds,
          is_completed: isCompleted || prev[currentVideo.id]?.is_completed
        }
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-background-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!module || videos.length === 0) {
    return (
      <div className="min-h-screen bg-background-900 flex items-center justify-center p-4">
        <div className="bg-background-800 border border-gray-700 rounded-lg p-12 text-center max-w-md">
          <PlayCircle size={64} className="mx-auto text-gray-600 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            Nenhum vídeo disponível
          </h2>
          <p className="text-gray-400 mb-6">
            Este módulo ainda não possui vídeos publicados
          </p>
          <Link
            to="/student"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium transition-all"
          >
            <ArrowLeft size={20} />
            Voltar para Módulos
          </Link>
        </div>
      </div>
    );
  }

  const completedCount = videos.filter(v => progress[v.id]?.is_completed).length;
  const moduleProgress = videos.length > 0 ? Math.round((completedCount / videos.length) * 100) : 0;

  // Encontrar índice do vídeo atual
  const currentVideoIndex = videos.findIndex(v => v.id === currentVideo?.id);
  const hasPrevious = currentVideoIndex > 0;
  const hasNext = currentVideoIndex < videos.length - 1;

  const goToPrevious = () => {
    if (hasPrevious) {
      loadVideo(videos[currentVideoIndex - 1].id);
    }
  };

  const goToNext = () => {
    if (hasNext) {
      loadVideo(videos[currentVideoIndex + 1].id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background-900">
      {/* Breadcrumb */}
      <div className="h-11 px-4 md:px-6 flex items-center justify-between border-b border-gray-200 dark:border-[#1f1f1f] bg-white dark:bg-background-800">
        <div className="flex items-center gap-2 text-[13px]">
          <Link to="/student" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            Módulos
          </Link>
          <ChevronRight size={14} className="text-gray-400 dark:text-gray-600" />
          <span className="text-gray-900 dark:text-white font-medium truncate">{module.title_pt}</span>
        </div>
      </div>

      {/* Layout: Responsivo */}
      <div className="flex flex-col lg:flex-row">
        {/* Coluna Principal */}
        <div className={`transition-all duration-300 ${theaterMode ? 'w-full' : 'flex-1 lg:max-w-[calc(100%-340px)]'}`}>
          {/* Player Container - Responsivo */}
          <div className="w-full">
            <VideoPlayer
              video={currentVideo}
              onProgressUpdate={handleProgressUpdate}
            />
          </div>

          {/* Botões de Controle - Responsivo */}
          <div className="bg-white dark:bg-background-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 md:py-4 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={goToPrevious}
              disabled={!hasPrevious}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition ${
                hasPrevious
                  ? 'bg-gray-100 dark:bg-background-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-background-600'
                  : 'bg-gray-100 dark:bg-background-700/50 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
            >
              <ChevronLeft size={16} className="md:w-[18px] md:h-[18px]" />
              <span className="hidden sm:inline">Lição Anterior</span>
              <span className="sm:hidden">Anterior</span>
            </button>

            <button
              onClick={() => setTheaterMode(!theaterMode)}
              className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium bg-gray-100 dark:bg-background-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-background-600 transition"
            >
              <Maximize2 size={16} className="md:w-[18px] md:h-[18px]" />
              <span className="hidden md:inline">{theaterMode ? 'Sair do Modo Teatro' : 'Modo Teatro'}</span>
              <span className="md:hidden">Teatro</span>
            </button>

            <button
              onClick={goToNext}
              disabled={!hasNext}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition ${
                hasNext
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-gray-100 dark:bg-background-700/50 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
            >
              <span className="hidden sm:inline">Próximo Material</span>
              <span className="sm:hidden">Próximo</span>
              <ChevronRight size={16} className="md:w-[18px] md:h-[18px]" />
            </button>
          </div>

          {/* Conteúdo abaixo do player */}
          <div className="p-4 md:p-6 space-y-4 md:space-y-6 bg-gray-50 dark:bg-background-900">
            {/* Título e Descrição */}
            {currentVideo && (
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {currentVideo.title_pt}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-[15px]">
                  {currentVideo.description_pt}
                </p>
              </div>
            )}

            {/* Avaliação */}
            {currentVideo && (
              <div className="bg-white dark:bg-background-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
                <VideoRating videoId={currentVideo.id} />
              </div>
            )}

            {/* Comentários */}
            {currentVideo && (
              <div className="bg-white dark:bg-background-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
                <VideoComments videoId={currentVideo.id} />
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Oculta no mobile e modo teatro */}
        {!theaterMode && (
          <div className="hidden lg:block lg:w-[340px] bg-white dark:bg-background-800 border-l border-gray-200 dark:border-gray-700 flex-col h-screen sticky top-0">
          {/* Progresso */}
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-success flex items-center justify-center flex-shrink-0">
              <Trophy size={20} className="text-success" />
            </div>
            <div>
              <div className="text-[22px] font-bold text-gray-900 dark:text-white leading-none mb-1">
                {moduleProgress}%
              </div>
              <div className="text-[13px] text-gray-600 dark:text-gray-400">
                Progresso
              </div>
            </div>
          </div>

          {/* Lista de Aulas */}
          <div className="flex-1 overflow-y-auto">
            {/* Seções */}
            {sections.sort((a, b) => a.order_index - b.order_index).map((section) => {
              const sectionVideos = videos
                .filter(v => v.section_id === section.id)
                .sort((a, b) => a.order_index - b.order_index);
              
              if (sectionVideos.length === 0) return null;
              
              const isExpanded = expandedSections.has(section.id);

              return (
                <div key={section.id} className="border-b border-gray-200 dark:border-gray-700">
                  {/* Section Header */}
                  <button
                    onClick={() => {
                      setExpandedSections(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(section.id)) {
                          newSet.delete(section.id);
                        } else {
                          newSet.add(section.id);
                        }
                        return newSet;
                      });
                    }}
                    className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-background-700/50 transition-colors"
                  >
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-500 dark:text-gray-400 transition-transform ${!isExpanded ? '-rotate-90' : ''}`}
                    />
                    <FolderOpen size={16} className="text-primary-500" />
                    <span className="text-[14px] font-semibold text-gray-900 dark:text-white flex-1 text-left">
                      {section.title_pt}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-600">
                      {sectionVideos.filter(v => progress[v.id]?.is_completed).length}/{sectionVideos.length}
                    </span>
                  </button>

                  {/* Section Videos */}
                  {isExpanded && sectionVideos.map((video) => {
                    const isCompleted = progress[video.id]?.is_completed;
                    const isCurrent = currentVideo?.id === video.id;
                    const watchedPercent = progress[video.id] && video.duration_seconds 
                      ? (progress[video.id].watched_seconds / video.duration_seconds) * 100 
                      : 0;
                    
                    return (
                      <div
                        key={video.id}
                        className={`transition-colors ${
                          isCurrent ? 'bg-gray-100 dark:bg-background-700' : 'hover:bg-gray-50 dark:hover:bg-background-700/50'
                        }`}
                      >
                        <button
                          onClick={() => loadVideo(video.id)}
                          className="w-full px-5 py-3 pl-12 flex items-center gap-3 text-left"
                        >
                          {/* Ícone Play */}
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCurrent ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-700'
                          }`}>
                            <PlayCircle size={12} className="text-white" />
                          </div>

                          {/* Título */}
                          <div className="flex-1 min-w-0">
                            <div className={`text-[13px] font-normal line-clamp-2 ${
                              isCurrent ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {video.title_pt}
                            </div>
                            {video.duration_seconds && (
                              <div className="text-[11px] text-gray-500 dark:text-gray-600 mt-0.5">
                                {Math.floor(video.duration_seconds / 60)} min
                              </div>
                            )}
                          </div>

                          {/* Checkbox de conclusão */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              if (isCompleted) {
                                setProgress(prev => ({
                                  ...prev,
                                  [video.id]: {
                                    ...prev[video.id],
                                    is_completed: false
                                  }
                                }));
                                
                                try {
                                  await api.post('/progress/complete', {
                                    video_id: video.id,
                                    is_completed: false
                                  });
                                } catch (error) {
                                  console.error('❌ Erro:', error);
                                  await loadModuleData();
                                }
                                return;
                              }
                              
                              if (watchedPercent < 90) {
                                alert(`Você precisa assistir pelo menos 90% da aula.\n\nProgresso: ${Math.round(watchedPercent)}%`);
                                return;
                              }
                              
                              setProgress(prev => ({
                                ...prev,
                                [video.id]: {
                                  ...prev[video.id],
                                  is_completed: true
                                }
                              }));
                              
                              try {
                                await api.post('/progress/complete', {
                                  video_id: video.id,
                                  is_completed: true
                                });
                              } catch (error) {
                                console.error('❌ Erro:', error);
                                await loadModuleData();
                              }
                            }}
                            className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
                              isCompleted
                                ? 'bg-success border-success'
                                : watchedPercent >= 90
                                ? 'border-primary-500 hover:bg-primary-500/20'
                                : 'border-gray-400 dark:border-gray-600 opacity-50 cursor-not-allowed'
                            }`}
                            title={
                              isCompleted 
                                ? 'Clique para desmarcar' 
                                : watchedPercent >= 90
                                ? 'Marcar como concluída'
                                : 'Assista 90% para desbloquear'
                            }
                          >
                            {isCompleted && (
                              <CheckCircle size={12} className="text-white" />
                            )}
                          </button>
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Vídeos sem seção */}
            {videos.filter(v => !v.section_id).length > 0 && (
              <div className="border-b border-gray-700">
                {videos
                  .filter(v => !v.section_id)
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((video) => {
                    const isCompleted = progress[video.id]?.is_completed;
                    const isCurrent = currentVideo?.id === video.id;
                    const watchedPercent = progress[video.id] && video.duration_seconds 
                      ? (progress[video.id].watched_seconds / video.duration_seconds) * 100 
                      : 0;
                    
                    return (
                      <div
                        key={video.id}
                        className={`transition-colors ${
                          isCurrent ? 'bg-background-700' : 'hover:bg-background-700/50'
                        }`}
                      >
                        <button
                          onClick={() => loadVideo(video.id)}
                          className="w-full px-5 py-3 flex items-center gap-3 text-left"
                        >
                          {/* Ícone Play */}
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCurrent ? 'bg-primary-500' : 'bg-gray-700'
                          }`}>
                            <PlayCircle size={12} className="text-white" />
                          </div>

                          {/* Título */}
                          <div className="flex-1 min-w-0">
                            <div className={`text-[13px] font-normal line-clamp-2 ${
                              isCurrent ? 'text-white' : 'text-gray-400'
                            }`}>
                              {video.title_pt}
                            </div>
                            {video.duration_seconds && (
                              <div className="text-[11px] text-gray-600 mt-0.5">
                                {Math.floor(video.duration_seconds / 60)} min
                              </div>
                            )}
                          </div>

                          {/* Checkbox de conclusão */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              if (isCompleted) {
                                setProgress(prev => ({
                                  ...prev,
                                  [video.id]: {
                                    ...prev[video.id],
                                    is_completed: false
                                  }
                                }));
                                
                                try {
                                  await api.post('/progress/complete', {
                                    video_id: video.id,
                                    is_completed: false
                                  });
                                } catch (error) {
                                  console.error('❌ Erro:', error);
                                  await loadModuleData();
                                }
                                return;
                              }
                              
                              if (watchedPercent < 90) {
                                alert(`Você precisa assistir pelo menos 90% da aula.\n\nProgresso: ${Math.round(watchedPercent)}%`);
                                return;
                              }
                              
                              setProgress(prev => ({
                                ...prev,
                                [video.id]: {
                                  ...prev[video.id],
                                  is_completed: true
                                }
                              }));
                              
                              try {
                                await api.post('/progress/complete', {
                                  video_id: video.id,
                                  is_completed: true
                                });
                              } catch (error) {
                                console.error('❌ Erro:', error);
                                await loadModuleData();
                              }
                            }}
                            className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
                              isCompleted
                                ? 'bg-success border-success'
                                : watchedPercent >= 90
                                ? 'border-primary-500 hover:bg-primary-500/20'
                                : 'border-gray-600 opacity-50 cursor-not-allowed'
                            }`}
                            title={
                              isCompleted 
                                ? 'Clique para desmarcar' 
                                : watchedPercent >= 90
                                ? 'Marcar como concluída'
                                : 'Assista 90% para desbloquear'
                            }
                          >
                            {isCompleted && (
                              <CheckCircle size={12} className="text-white" />
                            )}
                          </button>
                        </button>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
